/**
 * ConnectLinkedInButton
 * 
 * OAuth button for LinkedIn integration.
 * Handles:
 * - OAuth flow initiation
 * - Loading states
 * - Error handling
 * - Toasts notifications
 */

import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Loader2 } from 'lucide-react';
import { fetchWithAuth } from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConnectLinkedInButton({ 
  provider = 'linkedin',
  variant = 'default',
  className = '',
  onSuccess = null,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleConnect = async () => {
    setLoading(true);
    setError(null);

    try {
      // Start OAuth flow (cookies sent automatically)
      const response = await fetchWithAuth(
        `/api/auth/linkedin/start?provider=${provider}`,
        {
          method: 'POST',
          body: JSON.stringify({})
        }
      );

      if (!response.ok) {
        throw new Error('Impossible de démarrer l\'authentification');
      }

      const data = await response.json();

      if (data.success && data.data.authorization_url) {
        // Redirect to LinkedIn OAuth
        window.location.href = data.data.authorization_url;
      } else {
        throw new Error('Impossible de démarrer l\'authentification');
      }
    } catch (err) {
      console.error('LinkedIn OAuth start error:', err);
      const errorMessage = err.message || 'Erreur de connexion';
      setError(errorMessage);
      toast.error(`Erreur: ${errorMessage}`);
      setLoading(false);
    }
  };

  return (
    <div data-testid="connect-linkedin-container">
      <Button
        onClick={handleConnect}
        disabled={loading}
        variant={variant}
        className={`w-full relative overflow-hidden transition-all duration-300 ${className}`}
        data-testid="connect-linkedin-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Connexion en cours...
          </>
        ) : (
          <>
            <svg 
              className="w-5 h-5 mr-2" 
              fill="currentColor" 
              viewBox="0 0 24 24"
            >
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
            </svg>
            Connecter LinkedIn
          </>
        )}
      </Button>

      {error && (
        <p className="mt-2 text-sm text-red-400" data-testid="linkedin-error-message">
          {error}
        </p>
      )}
    </div>
  );
}
